import { type ReactNode } from 'react';
import { NavLink, Routes, Route, Navigate, useLocation } from 'react-router';
import { tabTriggerClass } from './tabTriggerClass';
import { CountBadge } from './CountBadge';

export interface TabItem {
  label: string;
  count?: number;
  link: string;
  component: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  basePath: string;
  title: string;
  subtitle?: ReactNode;
  onTabChange?: (tabId: string) => void;
  headerActions?: ReactNode;
}

export function Tabs({
  tabs,
  onTabChange,
  basePath,
  title,
  subtitle,
  headerActions,
}: TabsProps) {
  const { pathname } = useLocation();

  const firstLink = tabs.find((t) => t.link)?.link ?? '';
  const firstTabPath = normalize(firstLink, pathname, basePath);

  return (
    <div className="w-full">
      <div className="w-full bg-neut-bg-alt rounded-lg shadow-sm border border-neut-border">
        <div className="border-b border-neut-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl text-neut-prim font-semibold">{title}</h1>
              {subtitle}
            </div>
            {headerActions}
          </div>
        </div>

        <div className="flex items-center gap-2 px-6 py-2 overflow-x-auto">
          {tabs.map((tab) => (
            <NavLink
              key={tab.link}
              to={normalize(tab.link, pathname, basePath)}
              onClick={() => onTabChange?.(tab.link)}
              className={({ isActive }) =>
                `flex items-center gap-2 ${tabTriggerClass(isActive)}`
              }
              style={({ isActive }) =>
                ({
                  '--tab-count-bg': isActive
                    ? 'var(--color-tab-count-active-bg)'
                    : 'var(--color-neut-100)',
                  '--tab-count-color': isActive
                    ? 'var(--color-tab-count-active-text)'
                    : 'var(--color-neut-500)',
                }) as React.CSSProperties
              }
            >
              <span>{tab.label}</span>
              {typeof tab.count !== 'undefined' && (
                <CountBadge
                  count={tab.count}
                  className=""
                  weight="medium"
                  tabularNums={false}
                  style={{
                    backgroundColor: 'var(--tab-count-bg)',
                    color: 'var(--tab-count-color)',
                  }}
                />
              )}
            </NavLink>
          ))}
        </div>
      </div>
      <Routes>
        {tabs.map((t) => (
          <Route key={t.link} path={`${t.link}/*`} element={t.component} />
        ))}
        <Route path="*" element={<Navigate to={firstTabPath} replace />} />
      </Routes>
    </div>
  );
}

const normalize = (link: string, pathname: string, basePath?: string) => {
  const cleanLink = link.replace(/^\/+/, '');
  const cleanPathname = pathname.replace(/\/+$/, '');

  if (!basePath) {
    return `${cleanPathname}/${cleanLink}`.replace(/\/+/g, '/');
  }

  const cleanBase = basePath.replace(/^\/+|\/+$/g, '');
  const segments = cleanPathname.split('/').filter(Boolean);
  const idx = segments.lastIndexOf(cleanBase);

  // If basePath exists in the current path, slice up to it (inclusive); else use full pathname.
  const head =
    idx !== -1
      ? `/${segments.slice(0, idx + 1).join('/')}`
      : cleanPathname || '/';

  return `${head}/${cleanLink}`.replace(/\/+/g, '/');
};
